<template>
  <div v-if="hasAnyContent">
    <AthleteSectionSubheading>stats</AthleteSectionSubheading>
    <div
      class="flex flex-col justify-between w-full p-4 overflow-hidden bg-white rounded shadow"
      :class="{ 'mb-8': !isLoggedOut, 'mb-5': isLoggedOut }"
    >
      <!-- CSS GRID STATS  -->
      <div
        class="grid grid-cols-none gap-4 sm:grid-cols-2 md:grid-cols-4"
        style="align-items: end"
      >
        <AthletePositions
          class="w-full"
          v-if="positions && hasField"
          :sport="user.sport"
          :positions="user.positions"
          :gender="user.gender"
        />
        <!-- Text listing of positions if it's baseball, show the baseball positions -->
        <AthleteField
          label="position"
          class="w-full"
          v-if="user.positions.length"
          :style="hasField ? 'grid-column: 2 / -1;' : 'grid-column: 1 / -1;'"
        >
          <AthletePositionsText
            :sport="user.sport.name"
            :positions="user.positions"
          />
        </AthleteField>

        <!-- height -->
        <AthleteField label="height" class="w-full sm:w-1/3" v-if="user.height">
          <div class="flex flex-row">
            <p class="m-0 font-medium text-gray-900 ds-text-4xl">
              {{ user.height | formatHeight }}
              <span class="ds-text-4xl"></span>
            </p>
            <div class="relative flex flex-col items-end">
              <Height class="absolute bottom-0 left-0 w-auto h-8 -ml-3" />
            </div>
          </div>
        </AthleteField>

        <AthleteField label="weight" class="w-full pl-0" v-if="user.weight">
          <div class="flex flex-row">
            <p class="m-0 font-medium text-gray-900 ds-text-4xl">
              {{ user.weight }}
              <span class="ds-text-2xl">lbs</span>
            </p>
            <div class="relative flex flex-col items-end">
              <Weight class="absolute bottom-0 left-0 w-auto h-5 mb-2 ml-1" />
            </div>
          </div>
        </AthleteField>

        <AthleteDominantStatField
          label="dominant hand"
          v-if="dominantHand"
          :value="dominantHand.charAt(0).toUpperCase()"
          :iconComponent="dominantHandIconComponent"
        />

        <AthleteDominantStatField
          label="bats"
          v-if="bats"
          :value="bats.charAt(0).toUpperCase()"
          :iconComponent="BaseballDominant"
        />

        <AthleteDominantStatField
          label="throws"
          v-if="throws"
          :value="throws.charAt(0).toUpperCase()"
          :iconComponent="VolleyballDominant"
        />

        <AthleteDominantStatField
          label="dominant foot"
          v-if="dominantFoot"
          :value="dominantFoot.charAt(0).toUpperCase()"
          :iconComponent="SoccerDominant"
        />

        <AthleteDominantStatField
          label="shoots"
          v-if="shoots"
          :value="shoots.charAt(0).toUpperCase()"
          :iconComponent="BasketballDominant"
        />
      </div>

      <!-- Additional Stats -->
      <h6
        class="py-4 mt-2 mb-4 text-base font-bold text-gray-700 uppercase border-gray-300"
        style="border-bottom: solid 1px"
        v-if="organizedStats.speed.length"
      >
        Speed
      </h6>
      <AthleteStatsSecondary
        :stats="organizedStats.speed"
        v-if="organizedStats.speed.length"
      />
      <h6
        class="py-4 mt-2 mb-4 text-base font-bold text-gray-700 uppercase border-gray-300"
        style="border-bottom: solid 1px"
        v-if="organizedStats.time.length"
      >
        Time
      </h6>
      <AthleteStatsSecondary
        :stats="organizedStats.time"
        v-if="organizedStats.time.length"
      />
      <h6
        class="py-4 mt-2 mb-4 text-base font-bold text-gray-700 uppercase border-gray-300"
        style="border-bottom: solid 1px"
        v-if="organizedStats.additional.length"
      >
        Additional
      </h6>
      <AthleteStatsSecondary
        :stats="organizedStats.additional"
        v-if="organizedStats.additional.length"
      />
      <!-- Divider -->
      <div
        class="w-full mt-4 bg-gray-300 h-1px"
        v-if="organizedStats.fullWidth && organizedStats.fullWidth.length"
      />
      <AthleteStatsFullWidth
        :stats="organizedStats.fullWidth"
        v-if="organizedStats.fullWidth.length"
      />

      <!-- end of wrapper -->
    </div>

    <div
      class="mb-8"
      v-if="
        user.additionalStatistics || user.athleticHonors || user.otherSports
      "
    >
      <AthleteSectionSubheading
        >Additional Athletic Info</AthleteSectionSubheading
      >

      <div
        class="block px-4 py-4 overflow-hidden bg-white rounded shadow md:hidden"
      >
        <div class="grid grid-cols-none gap-4 sm:grid-cols-2 md:grid-cols-3">
          <div class="w-full" v-if="user.additionalStatistics">
            <MaxHeightExpandingContent :maxHeight="192">
              <div class="w-full md:mb-0 overflow-wrap">
                <h4 class="m-0 font-bold text-gray-700 uppercase ds-text-base">
                  Additional Stats
                </h4>
                <div class="w-full mt-3 mb-4 bg-gray-300 h-1px" />
                <p
                  class="m-0 font-normal ds-text-base list-fix"
                  v-html="user.additionalStatistics"
                ></p>
              </div>
            </MaxHeightExpandingContent>
          </div>

          <div class="w-full" v-if="user.athleticHonors">
            <MaxHeightExpandingContent :maxHeight="192">
              <div class="w-full md:mb-0 overflow-wrap">
                <h4
                  class="m-0 font-bold text-gray-700 uppercase ds-text-base"
                  :class="{ 'mt-6 sm:mt-0': user.additionalStatistics }"
                >
                  Athletic honors
                </h4>
                <div class="w-full mt-3 mb-4 bg-gray-300 h-1px" />
                <p
                  class="m-0 font-normal ds-text-base list-fix"
                  v-html="user.athleticHonors"
                ></p>
              </div>
            </MaxHeightExpandingContent>
          </div>

          <div class="w-full" v-if="user.otherSports">
            <MaxHeightExpandingContent :maxHeight="192">
              <div class="w-full overflow-wrap">
                <h4
                  class="m-0 font-bold text-gray-700 uppercase ds-text-base"
                  :class="{
                    'mt-6 sm:mt-0':
                      user.athleticHonors || user.additionalStatistics,
                  }"
                >
                  Other sports
                </h4>
                <div class="w-full mt-3 mb-4 bg-gray-300 h-1px" />
                <p
                  class="m-0 font-normal ds-text-base list-fix"
                  v-html="user.otherSports"
                ></p>
              </div>
            </MaxHeightExpandingContent>
          </div>
        </div>
      </div>

      <!-- Desktop View -->
      <MaxHeightExpandingCard :height="196" class="hidden w-full px-0 md:block">
        <div class="flex flex-row flex-wrap md:flex-no-wrap">
          <div
            class="w-full px-4 mb-6 md:mb-0 overflow-wrap"
            v-if="user.additionalStatistics"
          >
            <h4 class="m-0 font-bold text-gray-700 uppercase ds-text-base">
              Additional Stats
            </h4>
            <div class="w-full mt-3 mb-4 bg-gray-300 h-1px" />
            <p
              class="m-0 font-normal ds-text-base list-fix"
              v-html="user.additionalStatistics"
            ></p>
          </div>

          <div
            class="w-full px-4 mb-6 md:mb-0 overflow-wrap"
            v-if="user.athleticHonors"
          >
            <h4 class="m-0 font-bold text-gray-700 uppercase ds-text-base">
              Athletic honors
            </h4>
            <div class="w-full mt-3 mb-4 bg-gray-300 h-1px" />
            <p
              class="m-0 font-normal ds-text-base list-fix"
              v-html="user.athleticHonors"
            ></p>
          </div>

          <div class="w-full px-4 overflow-wrap" v-if="user.otherSports">
            <h4 class="m-0 font-bold text-gray-700 uppercase ds-text-base">
              Other sports
            </h4>
            <div class="w-full mt-3 mb-4 bg-gray-300 h-1px" />
            <p
              class="m-0 font-normal ds-text-base list-fix"
              v-html="user.otherSports"
            ></p>
          </div>
        </div>
      </MaxHeightExpandingCard>
    </div>
  </div>
</template>

<script>
import AthleteField from "../../AthleteField.vue";
import MaxHeightExpandingContent from "../MaxHeightExpandingContent.vue";
import MaxHeightExpandingCard from "../MaxHeightExpandingCard.vue";
import { mapState, mapGetters } from "vuex";
import AthleteSectionSubheading from "../../AthleteSectionSubheading.vue";
import AthletePositions from "./AthletePositions.vue";
import AthletePositionsText from "./AthletePositionsText.vue";
import AthleteDominantStatField from "./AthleteDominantStatField.vue";
import AthleteStatsSecondary from "./AthleteStatsSecondary.vue";
import AthleteStatsFullWidth from "./AthleteStatsFullWidth.vue";
import sortBy from "lodash/sortBy";
import pick from "lodash/pick";

import BaseballDominant from "@/srui/svg/BaseballDominant";
import BasketballDominant from "@/srui/svg/BasketballDominant";
import Height from "@/srui/svg/Height";
import LacrosseDominant from "@/srui/svg/LacrosseDominant";
import SoccerDominant from "@/srui/svg/SoccerDominant";
import Speed from "@/srui/svg/Speed";
import Time from "@/srui/svg/Time";
import VolleyballDominant from "@/srui/svg/VolleyballDominant";
import Weight from "@/srui/svg/Weight";

export default {
  components: {
    AthleteField,
    MaxHeightExpandingContent,
    MaxHeightExpandingCard,
    AthleteSectionSubheading,
    AthletePositions,
    AthletePositionsText,
    AthleteDominantStatField,
    AthleteStatsSecondary,
    AthleteStatsFullWidth,

    BaseballDominant,
    BasketballDominant,
    Height,
    LacrosseDominant,
    SoccerDominant,
    Speed,
    Time,
    VolleyballDominant,
    Weight,
  },
  computed: {
    ...mapState({
      user: (state) => state.athleteProfile.user,
    }),
    ...mapGetters({
      positions: "athleteProfile/allPositionsString",
      isLoggedOut: "userData/isLoggedOut",
    }),
    completeAthleticStats: function () {
      if (!this.user.athleticStats) {
        return null;
      }
      return sortBy(this.user.athleticStats, "sortOrder")
        .filter(
          (stat) =>
            stat.value != "" &&
            stat.value != null &&
            stat.slug != "bats" &&
            stat.slug != "throws" &&
            stat.slug != "dominant_foot" &&
            stat.slug != "shoots" &&
            stat.slug != "dominant_hand"
        )
        .map((stat) => {
          stat.unit_image = null;
          stat.unit_image_height = "h-5";

          switch (stat.unit) {
            case "inches":
            case "feet":
              stat.unit_icon_component =
                Height;
              stat.unit_image_height = "h-8";
              break;
            case "pounds":
              stat.unit_icon_component =
                Weight;

              break;
            case "seconds":
            case "time":
              stat.unit_icon_component =
                Time;
              break;
            case "mph":
              stat.unit_icon_component =
                Speed;
              break;
          }

          return stat;
        });
    },
    organizedStats() {
      const maxValLength = 14;
      const hasHtmlTagsOrDelim = (value) => /[<>\/|]/gm.test(value);
      const disallowedUnits = ["mph", "seconds"];
      return {
        speed: this.completeAthleticStats.filter((stat) => stat.unit === "mph"),
        time: this.completeAthleticStats.filter(
          (stat) => stat.unit === "seconds"
        ),
        additional: this.completeAthleticStats.filter(
          (stat) =>
            stat.value.toString().length <= maxValLength &&
            !disallowedUnits.includes(stat.unit) &&
            !hasHtmlTagsOrDelim(stat.value)
        ),
        fullWidth: this.completeAthleticStats.filter(
          (stat) =>
            stat.value.length > maxValLength || hasHtmlTagsOrDelim(stat.value)
        ),
      };
    },
    bats: function () {
      if (!this.user.athleticStats) {
        return null;
      }

      const batsStat = this.user.athleticStats.find(
        (stat) => stat.slug == "bats"
      );

      return batsStat ? batsStat.value : null;
    },
    throws: function () {
      if (!this.user.athleticStats) {
        return null;
      }

      const throwsStat = this.user.athleticStats.find(
        (stat) => stat.slug == "throws"
      );

      return throwsStat ? throwsStat.value : null;
    },
    dominantFoot: function () {
      if (!this.user.athleticStats) {
        return null;
      }

      const footStat = this.user.athleticStats.find(
        (stat) => stat.slug == "dominant_foot"
      );

      return footStat ? footStat.value : null;
    },
    dominantHand: function () {
      if (!this.user.athleticStats) {
        return null;
      }

      const handStat = this.user.athleticStats.find(
        (stat) => stat.slug == "dominant_hand"
      );

      return handStat ? handStat.value : null;
    },
    shoots: function () {
      if (!this.user.athleticStats) {
        return null;
      }

      const shootsStat = this.user.athleticStats.find(
        (stat) => stat.slug == "shoots"
      );

      return shootsStat ? shootsStat.value : null;
    },
    dominantHandIconComponent: function () {
      if (
        this.dominantHand &&
        this.user.sport &&
        this.user.sport.name &&
        this.user.sport.name == "lacrosse"
      ) {
        return LacrosseDominant;
      }

      return VolleyballDominant;
    },
    additonalCount: function () {
      let count = 0;

      if (this.user.additionalStatistics) {
        count++;
      }

      if (this.user.athleticHonors) {
        count++;
      }

      if (this.user.otherSports) {
        count++;
      }

      return count;
    },
    hasField() {
      return [
        "volleyball",
        "soccer",
        "lacrosse",
        "field hockey",
        "basketball",
        "baseball",
        "softball",
      ].includes(this.user.sport.name);
    },

    hasAnyContent() {
      return !!(
        this.user.height ||
        this.user.weight ||
        (this.user.athleticStats && this.user.athleticStats.length) ||
        this.user.positions.length
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.m-0 * {
  margin: 0 !important;
}
.list-fix {
  /deep/ ul {
    padding-left: 18px !important;
  }

  /deep/ ol {
    padding-left: 18px;
  }
}

.overflow-wrap {
  overflow-wrap: anywhere;
}
</style>
